import PageHero from "@/components/PageHero";
import CTASection from "@/components/CTASection";
import { DIRECTORS, COMPANY } from "@/data/content";
import { FileCheck2, ShieldCheck, Award, Building2, FileBadge, Stamp } from "lucide-react";

const CERTS = [
  { icon: FileCheck2, label: "Corporate Identification Number (CIN)", value: COMPANY.cin, mono: true },
  { icon: Stamp, label: "GST Registration", value: COMPANY.gst, mono: true },
  { icon: FileBadge, label: "MSME / UDYAM", value: COMPANY.msme, mono: true },
  { icon: Award, label: "ISO Certifications", value: COMPANY.iso },
  { icon: ShieldCheck, label: "HSE Compliance", value: "EHS framework aligned with IS / OHSAS standards" },
  { icon: Building2, label: "Registered Office", value: `${COMPANY.address.line1}, ${COMPANY.address.line2}, ${COMPANY.address.city} ${COMPANY.address.pin}` },
];

export default function Credibility() {
  return (
    <div data-testid="credibility-page">
      <PageHero
        overline="Corporate Credibility"
        title="Compliance-first. Investor-grade. Transparent."
        subtitle="Every project we sign is backed by a fully compliant corporate entity, robust governance and disciplined disclosures."
        image="https://images.unsplash.com/photo-1542744173-8e7e53415bb0?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000"
        testid="credibility-hero"
      />

      <section className="section-padding bg-white">
        <div className="container-corp">
          <div className="overline mb-4">Certifications & Registrations</div>
          <h2 className="font-heading text-3xl lg:text-4xl font-semibold text-[#0B2341] mb-14 max-w-2xl leading-tight">
            Corporate registrations & certifications.
          </h2>
          <div className="grid sm:grid-cols-2 gap-px bg-[#E2E8F0]">
            {CERTS.map((c, i) => (
              <div key={c.label} data-testid={`cert-${i}`} className="bg-white p-8">
                <c.icon className="text-[#16A34A] mb-5" size={28} strokeWidth={1.5} />
                <div className="text-xs uppercase tracking-[0.2em] text-[#4A5568] mb-2">{c.label}</div>
                <div className={`text-[#0B2341] ${c.mono ? "font-mono" : "font-medium"}`}>{c.value}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section-padding bg-[#F8F9FA]">
        <div className="container-corp">
          <div className="overline mb-4">Governance</div>
          <h2 className="font-heading text-3xl lg:text-4xl font-semibold text-[#0B2341] mb-14 max-w-2xl leading-tight">
            Board of Directors.
          </h2>
          <div className="grid md:grid-cols-3 gap-px bg-[#E2E8F0]">
            {DIRECTORS.map((d, i) => (
              <div key={i} className="bg-white p-8" data-testid={`cred-director-${i}`}>
                <div className="aspect-[4/5] bg-[#0B2341] mb-6 flex items-center justify-center">
                  <span className="text-[#16A34A] font-heading text-6xl font-light">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                </div>
                <div className="text-xs uppercase tracking-[0.2em] text-[#16A34A] mb-2">{d.role}</div>
                <h3 className="font-heading text-xl font-semibold text-[#0B2341] mb-3">{d.name}</h3>
                <p className="text-sm text-[#4A5568] leading-relaxed">{d.bio}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section-padding bg-[#0B2341] text-white">
        <div className="container-corp grid md:grid-cols-3 gap-8">
          {[
            { k: "Statutory Audits", v: "Annual statutory audits by a reputed CA firm." },
            { k: "Tax Compliance", v: "Up-to-date GST, TDS and income tax compliance." },
            { k: "HSE Records", v: "Zero LTI safety record across multi-state EPC sites." },
          ].map((b, i) => (
            <div key={b.k} data-testid={`compliance-${i}`} className="border-l-2 border-[#16A34A] pl-6">
              <div className="overline mb-3">{b.k}</div>
              <p className="text-white/80 leading-relaxed">{b.v}</p>
            </div>
          ))}
        </div>
      </section>

      <CTASection
        overline="Due diligence ready"
        title="Need our full corporate dossier?"
        body="Request CIN/GST/ISO copies, board profile, audited financials and project completion certificates for due-diligence."
      />
    </div>
  );
}
